import React, { useState, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from 'services/useClickOutside';

const PANEL_MAX_H = 200;

const getPortalStyle = (buttonEl) => {
  if (!buttonEl) return {};
  const rect = buttonEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const openAbove = spaceBelow < PANEL_MAX_H && rect.top > spaceBelow;
  const left = Math.min(rect.left, window.innerWidth - rect.width - 8);
  return {
    position: 'fixed',
    top: openAbove ? `${rect.top - PANEL_MAX_H - 8}px` : `${rect.bottom + 4}px`,
    left: `${Math.max(8, left)}px`,
    width: `${rect.width}px`,
    maxHeight: `${PANEL_MAX_H}px`,
    zIndex: 70,
  };
};

/**
 * Dropdown + free text input hybrid: suggests existing `options` (filtered as the
 * user types) but never forces selection from a closed list — whatever text is
 * in the field on confirm (Enter or blur) becomes the value. Used where the field
 * is backed by an open CharField and options grow organically (e.g. Health `area`).
 *
 * Not for filters over a fixed/known set — use a plain dropdown there instead.
 */
const DropdownWithInput = ({
  value = '',
  onChange,
  options = [],
  placeholder = 'Type or select',
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  // Keep the draft in sync when the external value changes while closed
  // (e.g. switching between records in the modal).
  useEffect(() => {
    if (!isOpen) setDraft(value);
  }, [value, isOpen]);

  const filteredOptions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    const unique = [...new Set(options.filter(Boolean))];
    if (!q) return unique;
    return unique.filter((o) => o.toLowerCase().includes(q));
  }, [options, draft]);

  const commit = (val) => {
    const finalValue = (val ?? draft).trim();
    onChange(finalValue);
    setDraft(finalValue);
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const cancel = () => {
    setDraft(value);
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  useClickOutside([inputRef, panelRef], () => commit(), isOpen);

  const handleFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightIndex(-1);
  };

  const handleInputChange = (e) => {
    setDraft(e.target.value);
    setHighlightIndex(-1);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelectOption = (option) => commit(option);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredOptions.length === 0) return;
      setHighlightIndex((i) => (i + 1) % filteredOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredOptions.length === 0) return;
      setHighlightIndex((i) => (i <= 0 ? filteredOptions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredOptions[highlightIndex]) {
        commit(filteredOptions[highlightIndex]);
      } else {
        commit();
      }
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
      inputRef.current?.blur();
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={handleFocus}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className={`w-full rounded-lg px-2 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 focus:bg-white border-0 focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      />

      {isOpen &&
        filteredOptions.length > 0 &&
        createPortal(
          <div
            ref={panelRef}
            style={getPortalStyle(inputRef.current)}
            className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-y-auto p-1"
            onMouseDown={(e) => e.preventDefault()}
          >
            {filteredOptions.map((option, idx) => (
              <button
                key={option}
                type="button"
                onClick={() => handleSelectOption(option)}
                className={`w-full flex items-center px-2 py-1.5 rounded text-left text-xs hover:bg-gray-100 ${
                  idx === highlightIndex ? 'bg-gray-100' : ''
                } ${option === value ? 'font-medium text-gray-900' : 'text-gray-700'}`}
              >
                {option}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default DropdownWithInput;
